/**
 * The upload contract, narrowed from `unknown` at the boundary.
 *
 * Mirrors `AdminUploadForm.d.ts` and `ExtractionResult.d.ts`. The error union
 * is the important half: the interface picks severity from the kind, so a
 * free-text message would collapse three very different outcomes into one red
 * box.
 */

export type UploadFormat = 'pdf' | 'docx' | 'doc' | 'txt';
export type Publisher = 'oman-awqaf' | 'uae-awqaf' | 'none';

export type SectionKind =
  | 'opening'
  | 'first-khutbah'
  | 'second-khutbah'
  | 'supplication';

export type PassageType =
  | 'opening'
  | 'body'
  | 'quran'
  | 'hadith'
  | 'dua'
  | 'closing';

export interface ExtractedPassage {
  readonly n: number;
  readonly page: number | null;
  readonly section: SectionKind;
  readonly type: PassageType;
  /** Tashkeel intact, never styled. */
  readonly ar: string;
  readonly ref: { label: string; confirmed: boolean } | null;
}

export interface ExtractionResult {
  readonly khutbahId: string;
  readonly sourceVersionId: string;
  readonly format: UploadFormat;
  readonly formatNote: string | null;
  readonly passages: readonly ExtractedPassage[];
  readonly sections: readonly { kind: SectionKind; count: number }[];
  /** Null means the format cannot identify them — plain text has no fonts. */
  readonly quran: number | null;
  readonly hadith: number | null;
  readonly arabicRatio: number | null;
  readonly warnings: readonly { code: string; text: string }[];
  readonly provenance: {
    source: 'publisher' | 'user';
    publisher: string | null;
    translation: 'none';
  };
  readonly blocked: { kind: 'budget'; resetsOn: string } | null;
}

/**
 * Why an upload was refused.
 *
 * The three severities the mock specifies map straight off `kind`:
 * `format`/`size`/`pages` cannot proceed, `encoding` is fixable and leads with
 * what to do, `duplicate` is not a failure at all.
 */
export type UploadError =
  | { kind: 'format'; detected?: string; message: string }
  | { kind: 'encoding'; detected?: string; message: string }
  | { kind: 'size'; bytes: number; limitBytes: number; message: string }
  | { kind: 'pages'; pages: number; limitPages: number; message: string }
  | { kind: 'duplicate'; khutbahId: string; message: string }
  /**
   * A *different* file for a Friday this publisher already has. Not a
   * failure — a decision only the editor can make, so it carries what they
   * need to make it: which khutbah, and whether readers can already see it.
   */
  | {
      kind: 'same-friday';
      khutbahId: string;
      friday: string;
      published: boolean;
      message: string;
    }
  /** Replacement refused because the existing khutbah is live for readers. */
  | { kind: 'published'; khutbahId: string; message: string }
  /**
   * Not signed in to the editor surface.
   *
   * Derived from the status, not sent by the server: a 401 has no body worth
   * parsing. It needs its own kind because a background `fetch` that gets a
   * 401 does **not** raise the browser's credential prompt the way a
   * navigation does — so without this the editor sees "the server returned
   * 401" and has no idea they simply need to sign in.
   */
  | { kind: 'auth'; message: string }
  | { kind: 'network'; message: string };

export type Severity = 'destructive' | 'warning' | 'info';

/**
 * How loudly to say it.
 *
 * An encoding problem is the editor's file being in the wrong character set —
 * fixable in a minute, and shouting about it would be wrong. A duplicate is
 * not a problem at all: the khutbah is already here.
 */
export function severityOf(error: UploadError): Severity {
  switch (error.kind) {
    case 'duplicate':
      return 'info';
    // Both are decisions rather than failures. An encoding problem is a
    // minute's work and nothing about the khutbah is wrong; a Friday that is
    // already taken is usually a corrected khutbah, occasionally the wrong
    // date. A red box would misdescribe either one.
    case 'encoding':
    case 'same-friday':
      return 'warning';
    default:
      return 'destructive';
  }
}

export class ResponseShapeError extends Error {
  constructor(what: string) {
    super(`The server sent something this version cannot read: ${what}`);
    this.name = 'ResponseShapeError';
  }
}

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ResponseShapeError(`${where} is not an object`);
  }
  return value as Record<string, unknown>;
}

function str(source: Record<string, unknown>, key: string, where: string): string {
  const value = source[key];
  if (typeof value !== 'string') {
    throw new ResponseShapeError(`${where}.${key} is not a string`);
  }
  return value;
}

function strOrNull(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Deliberately returns null for a missing number rather than 0.
 *
 * The contract uses null for "this format cannot identify quotations at all",
 * which is a different answer from "none present". Coercing to 0 would tell
 * the editor a plain-text khutbah contains no Qur'an, which is a claim nothing
 * has established.
 */
function numOrNull(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function parseExtractionResult(value: unknown): ExtractionResult {
  const source = asRecord(value, 'response');
  const passages = source.passages;
  const sections = source.sections;
  if (!Array.isArray(passages) || !Array.isArray(sections)) {
    throw new ResponseShapeError('passages or sections is not an array');
  }

  return {
    khutbahId: str(source, 'khutbahId', 'response'),
    sourceVersionId: str(source, 'sourceVersionId', 'response'),
    format: (strOrNull(source, 'format') ?? 'txt') as UploadFormat,
    formatNote: strOrNull(source, 'formatNote'),
    passages: passages.map((entry, index) => {
      const p = asRecord(entry, `passages[${index}]`);
      const ref = p.ref ? asRecord(p.ref, `passages[${index}].ref`) : null;
      return {
        n: numOrNull(p, 'n') ?? index + 1,
        page: numOrNull(p, 'page'),
        section: (strOrNull(p, 'section') ?? 'opening') as SectionKind,
        type: (strOrNull(p, 'type') ?? 'body') as PassageType,
        ar: strOrNull(p, 'ar') ?? '',
        ref: ref
          ? { label: str(ref, 'label', 'ref'), confirmed: ref.confirmed === true }
          : null,
      };
    }),
    sections: sections.map((entry, index) => {
      const s = asRecord(entry, `sections[${index}]`);
      return {
        kind: (strOrNull(s, 'kind') ?? 'opening') as SectionKind,
        count: numOrNull(s, 'count') ?? 0,
      };
    }),
    quran: numOrNull(source, 'quran'),
    hadith: numOrNull(source, 'hadith'),
    arabicRatio: numOrNull(source, 'arabicRatio'),
    warnings: Array.isArray(source.warnings)
      ? source.warnings.map((entry, index) => {
          const w = asRecord(entry, `warnings[${index}]`);
          return {
            code: strOrNull(w, 'code') ?? 'other',
            text: strOrNull(w, 'text') ?? '',
          };
        })
      : [],
    provenance: {
      source:
        asRecord(source.provenance ?? {}, 'provenance').source === 'user'
          ? 'user'
          : 'publisher',
      publisher: strOrNull(asRecord(source.provenance ?? {}, 'provenance'), 'publisher'),
      translation: 'none',
    },
    blocked:
      source.blocked && typeof source.blocked === 'object'
        ? {
            kind: 'budget',
            resetsOn: strOrNull(asRecord(source.blocked, 'blocked'), 'resetsOn') ?? '',
          }
        : null,
  };
}

/** Turns an RFC 9457 problem body into the typed error the interface needs. */
export function parseUploadError(status: number, body: unknown): UploadError {
  const problem =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)
      : {};
  const message =
    typeof problem.detail === 'string'
      ? problem.detail
      : `The server returned ${status}.`;
  // Status first: an unauthenticated request never reaches the handler that
  // would have given the body a `kind`.
  if (status === 401 || status === 403) {
    return {
      kind: 'auth',
      message:
        'Uploading needs editor credentials, and this page does not have them yet.',
    };
  }

  const kind = typeof problem.kind === 'string' ? problem.kind : null;

  switch (kind) {
    case 'duplicate':
      return {
        kind: 'duplicate',
        khutbahId: typeof problem.khutbahId === 'string' ? problem.khutbahId : '',
        message,
      };
    case 'encoding':
      return { kind: 'encoding', message };
    case 'same-friday':
      return {
        kind: 'same-friday',
        khutbahId: typeof problem.khutbahId === 'string' ? problem.khutbahId : '',
        friday: typeof problem.friday === 'string' ? problem.friday : '',
        // Defaults to true, the safer reading: if the server did not say,
        // the interface must not offer to replace something that may be live.
        published: problem.published !== false,
        message,
      };
    case 'published':
      return {
        kind: 'published',
        khutbahId: typeof problem.khutbahId === 'string' ? problem.khutbahId : '',
        message,
      };
    case 'size':
      return {
        kind: 'size',
        bytes: typeof problem.bytes === 'number' ? problem.bytes : 0,
        limitBytes: typeof problem.limitBytes === 'number' ? problem.limitBytes : 0,
        message,
      };
    case 'pages':
      return {
        kind: 'pages',
        pages: typeof problem.pages === 'number' ? problem.pages : 0,
        limitPages: typeof problem.limitPages === 'number' ? problem.limitPages : 0,
        message,
      };
    case 'format':
      return {
        kind: 'format',
        // Spread rather than `detected: undefined`: under
        // exactOptionalPropertyTypes an absent property and one explicitly set
        // to undefined are different types, and "we were not told" is the
        // honest shape for a format the server could not name.
        ...(typeof problem.detected === 'string' ? { detected: problem.detected } : {}),
        message,
      };
    default:
      // An unrecognised kind is still a refusal; reporting it as a network
      // problem is the honest fallback rather than inventing a severity.
      return { kind: 'network', message };
  }
}
